import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { ArrowRight, Upload } from 'lucide-react';
import { C } from '../styles/tokens';
import { WaveThumb, Ico } from './ui/Icons';
import { useRecentContent } from '../http-hooks/content/useRecentContent';
import formatAudioFileDuration from '../utils/AudioDuration';

const VIEW_KEY = 'l2i_recent_view';

const HEADERS = [
    { label: 'Title',    cls: '' },
    { label: 'Duration', cls: 'ct-col-duration' },
    { label: 'Created',  cls: 'ct-col-created' },
    { label: 'Status',   cls: '' },
];

const StatusBadge = ({ isAudioDisabled, fullAudioStatus, publishedAt }) => {
    const base = { borderRadius: 999, fontSize: 10.5, padding: '3px 8px', fontWeight: 600, display: 'inline-block' };
    if (isAudioDisabled)
        return <span style={{ ...base, background: '#FEE2E2', color: '#DC2626' }}>Disabled</span>;
    if (fullAudioStatus === 'IN PROGRESS')
        return <span style={{ ...base, background: C.p100, color: C.p700 }}>In Progress</span>;
    if (publishedAt === undefined || publishedAt)
        return <span style={{ ...base, background: C.g50, color: C.g600 }}>Published</span>;
    return <span style={{ ...base, background: C.a50, color: C.a500 }}>Draft</span>;
};

const ROW_STYLE = {
    padding: '10px 16px',
    alignItems: 'center',
    background: 'transparent',
    transition: 'background .1s',
    outline: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
};

// Best-effort plain-text excerpt from an item's script body (HTML string).
function excerptOf(item) {
    const src = item?.body;
    if (typeof src !== 'string' || !src) return '';
    let text = src;
    if (typeof document !== 'undefined') {
        const tmp = document.createElement('div');
        tmp.innerHTML = src;
        text = tmp.textContent || tmp.innerText || '';
    }
    return text.replace(/\s+/g, ' ').trim();
}

function SkeletonRow({ isLast }) {
    return (
        <div className="recent-files-row" style={{
            padding: '12px 16px', alignItems: 'center',
            borderBottom: isLast ? 'none' : `1px solid ${C.n100}`,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="shine" style={{ width: 32, height: 32, borderRadius: 7, flexShrink: 0 }} />
                <div>
                    <div className="shine rounded-2" style={{ height: 13, width: 150, marginBottom: 5 }} />
                    <div className="shine rounded-2" style={{ height: 10, width: 55 }} />
                </div>
            </div>
            <div className="ct-col-duration shine rounded-2" style={{ height: 12, width: 36 }} />
            <div className="ct-col-created shine rounded-2" style={{ height: 12, width: 70 }} />
            <div className="shine rounded-5" style={{ height: 20, width: 65 }} />
        </div>
    );
}

function SkeletonCard() {
    return (
        <div style={{ background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13, padding: 16, height: 168 }}>
            <div className="shine rounded-5" style={{ height: 20, width: 70, marginBottom: 12, marginLeft: 'auto' }} />
            <div className="shine rounded-2" style={{ height: 11, width: '100%', marginBottom: 6 }} />
            <div className="shine rounded-2" style={{ height: 11, width: '90%', marginBottom: 6 }} />
            <div className="shine rounded-2" style={{ height: 11, width: '60%', marginBottom: 18 }} />
            <div className="shine rounded-2" style={{ height: 13, width: 140, marginBottom: 6 }} />
            <div className="shine rounded-2" style={{ height: 10, width: 110 }} />
        </div>
    );
}

// ── View-toggle icon buttons ─────────────────────────────────
function ViewToggle({ view, onChange }) {
    const Btn = ({ id, children }) => (
        <button onClick={() => onChange(id)} aria-label={`${id} view`}
            style={{
                width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', background: view === id ? C.n100 : C.w, cursor: 'pointer',
            }}>
            {children}
        </button>
    );
    return (
        <div style={{ display: 'flex', border: `1px solid ${C.n200}`, borderRadius: 8, overflow: 'hidden', background: C.w }}>
            <Btn id="grid">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={view === 'grid' ? C.n700 : C.n500} strokeWidth="1.5">
                    <rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" />
                    <rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" />
                </svg>
            </Btn>
            <Btn id="list">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={view === 'list' ? C.n700 : C.n500} strokeWidth="1.5">
                    <path d="M2 4h12M2 8h12M2 12h12" strokeLinecap="round" />
                </svg>
            </Btn>
        </div>
    );
}

export default function RecentFiles({ org_id, integration_id }) {
    const history = useHistory();
    const { data: items = [], isLoading } = useRecentContent({ org_id, integration_id });

    const [view, setView] = useState(() => {
        try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch (e) { return 'grid'; }
    });
    const setViewPersist = (v) => {
        try { localStorage.setItem(VIEW_KEY, v); } catch (e) { /* ignore */ }
        setView(v);
    };

    const allFilesPath = `/organisation/${org_id}/integration/${integration_id}/content`;
    const newPath = `/organisation/${org_id}/integration/${integration_id}/content/new`;

    const goTo = (item) => {
        const contentId = item.SK.split('#')[1];
        history.push(`/organisation/${org_id}/integration/${integration_id}/content/${contentId}`);
    };

    return (
        <div style={{ marginTop: 32 }}>
            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 13, gap: 10, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: C.n900, letterSpacing: '-.01em', margin: 0 }}>
                    Recent projects
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ViewToggle view={view} onChange={setViewPersist} />
                    <button
                        onClick={() => history.push(allFilesPath)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            fontSize: 13, fontWeight: 600, color: C.p600,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        }}
                    >
                        All Projects <ArrowRight size={14} />
                    </button>
                </div>
            </div>

            {/* ── GRID VIEW ── */}
            {view === 'grid' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>

                    {/* New project card */}
                    {!isLoading && (
                        <button
                            onClick={() => history.push(newPath)}
                            style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                                minHeight: 168, background: C.p50, border: `1.5px dashed ${C.p200}`, borderRadius: 13,
                                cursor: 'pointer', color: C.p600, textAlign: 'center', padding: 16,
                            }}
                        >
                            <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.p100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Ico n="plus" size={18} color={C.p600} />
                            </div>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>New project</div>
                                <div style={{ fontSize: 11.5, color: C.n500, marginTop: 2 }}>Start with a blank script</div>
                            </div>
                        </button>
                    )}

                    {isLoading && [0, 1, 2].map(i => <SkeletonCard key={i} />)}

                    {!isLoading && items.map((item) => {
                        const excerpt = excerptOf(item);
                        const words = excerpt ? excerpt.split(' ').length : null;
                        return (
                            <button
                                key={item.SK}
                                onClick={() => goTo(item)}
                                style={{
                                    display: 'flex', flexDirection: 'column', minHeight: 168,
                                    background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13,
                                    padding: 16, cursor: 'pointer', textAlign: 'left', transition: 'box-shadow .12s, border-color .12s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(15,23,42,.06)'; e.currentTarget.style.borderColor = C.n300; }}
                                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = C.n200; }}
                            >
                                {/* Status */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                                    <StatusBadge
                                        publishedAt={item.published_at}
                                        fullAudioStatus={item.full_audio_status}
                                        isAudioDisabled={item.is_audio_disabled}
                                    />
                                </div>

                                {/* Excerpt */}
                                <div style={{
                                    flex: 1, fontSize: 12.5, color: C.n500, lineHeight: 1.55, marginBottom: 12,
                                    overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                                }}>
                                    {excerpt || 'No preview available for this project.'}
                                </div>

                                {/* Footer: accent bar + title + meta */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                                    <div style={{ width: 3, alignSelf: 'stretch', minHeight: 30, borderRadius: 2, background: C.p500, flexShrink: 0 }} />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: C.n900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                                            {item.title}
                                        </div>
                                        <div style={{ fontSize: 11.5, color: C.n400 }}>
                                            {item.audio_duration ? formatAudioFileDuration(item.audio_duration) : '—'}
                                            {words ? ` · ${words.toLocaleString()} words` : ''}
                                            {item.created_at ? ` · ${moment(item.created_at).format('MMM D')}` : ''}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}

                    {/* Empty (grid) — new-project card already shown, so nothing more needed */}
                </div>
            )}

            {/* ── LIST VIEW ── */}
            {view === 'list' && (
                <div style={{ background: C.w, border: `1px solid ${C.n200}`, borderRadius: 13, overflow: 'hidden' }}>

                    {/* Header */}
                    <div className="recent-files-row" style={{
                        padding: '9px 16px', borderBottom: `1px solid ${C.n200}`, background: '#FAFAFA',
                    }}>
                        {HEADERS.map((h, i) => (
                            <div key={i} className={h.cls}
                                style={{ fontSize: 11.5, fontWeight: 600, color: C.n400, letterSpacing: '.03em' }}>
                                {h.label}
                            </div>
                        ))}
                    </div>

                    {/* Loading */}
                    {isLoading && [0, 1, 2, 3, 4].map(i => <SkeletonRow key={i} isLast={i === 4} />)}

                    {/* Empty */}
                    {!isLoading && items.length === 0 && (
                        <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                            <div style={{ fontSize: 13, color: C.n400, marginBottom: 10 }}>No voiceovers yet</div>
                            <button
                                onClick={() => history.push(newPath)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                    padding: '7px 16px', borderRadius: 8,
                                    background: C.p600, color: C.w,
                                    border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                }}
                            >
                                <Ico n="plus" size={13} color={C.w} /> Create your first voiceover
                            </button>
                        </div>
                    )}

                    {/* Rows */}
                    {!isLoading && items.map((item, i) => {
                        const isUploadedAudio = !(item.body) && !(item.slate_body);
                        const isLast = i === items.length - 1;

                        return (
                            <button
                                key={item.SK}
                                onClick={() => goTo(item)}
                                className="recent-files-row"
                                style={{
                                    ...ROW_STYLE,
                                    borderBottom: isLast ? 'none' : `1px solid ${C.n100}`,
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = C.n50}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                                {/* Title */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                    <div style={{
                                        width: 32, height: 32, background: C.p50, borderRadius: 7,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                    }}>
                                        {isUploadedAudio
                                            ? <Upload size={14} color={C.p500} strokeWidth={1.75} />
                                            : <WaveThumb />
                                        }
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 13.5, fontWeight: 600, color: C.n900, marginBottom: 1,
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                        }}>
                                            {item.title}
                                        </div>
                                        <div style={{ fontSize: 10.5, color: C.n400 }}>
                                            {item.full_audio_status === 'IN PROGRESS'
                                                ? 'Processing…'
                                                : isUploadedAudio ? 'Uploaded' : 'Voiceover'}
                                        </div>
                                    </div>
                                </div>

                                {/* Duration */}
                                <div className="ct-col-duration" style={{ fontSize: 12.5, color: C.n600 }}>
                                    {item.audio_duration ? formatAudioFileDuration(item.audio_duration) : '—'}
                                </div>

                                {/* Created */}
                                <div className="ct-col-created" style={{ fontSize: 12.5, color: C.n500 }}>
                                    {item.created_at ? moment(item.created_at).format('MMM D, YYYY') : '—'}
                                </div>

                                {/* Status */}
                                <div>
                                    <StatusBadge
                                        publishedAt={item.published_at}
                                        fullAudioStatus={item.full_audio_status}
                                        isAudioDisabled={item.is_audio_disabled}
                                    />
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
